<template>
  <section class="dp-layout internal-directory-page">
    <section class="dp-left">
      <DepartmentPane
        ref="departmentPane"
        directory-scope="internal"
        pane-title="内部通讯录"
        :default-select-root="true"
        @tree-node-click="treeNodeClick"
      />
    </section>
    <section class="dp-right">
      <div class="internal-workspace">
        <header class="internal-toolbar">
          <div class="internal-context">
            <span class="internal-title-icon" aria-hidden="true"><i class="el-icon-s-custom"></i></span>
            <div>
              <h1>内部通讯录管理</h1>
              <p class="internal-subtitle">对齐钉钉企业通讯录：独立行政部门树，默认不含校区；校区仅在教学架构/家校通讯录中维护</p>
              <div class="internal-breadcrumb">
                <span>当前挂载</span>
                <i class="el-icon-arrow-right"></i>
                <strong>{{ currentNode ? currentNode.label : '请选择左侧学校、校区或部门' }}</strong>
                <el-tag v-if="currentNode" size="mini" type="info" class="internal-type-tag">
                  {{ nodeTypeText(currentNode) }}
                </el-tag>
              </div>
            </div>
          </div>
          <div class="internal-actions">
            <template v-if="isDingTalkSchool">
              <el-button type="success" plain icon="el-icon-refresh" @click="openSyncDialog">同步钉钉</el-button>
              <el-button icon="el-icon-s-operation" @click="openTaskCenter">钉钉同步中心</el-button>
            </template>
            <el-button v-if="canManageCurrent" icon="el-icon-edit-outline" @click="openEditCurrent">编辑当前部门</el-button>
            <el-button v-if="canManageCurrent" type="danger" plain icon="el-icon-delete" :loading="removing"
              @click="removeCurrent">删除当前部门</el-button>
          </div>
        </header>

        <div class="internal-list-panel">
          <div class="internal-section-heading">
            <div>
              <h2>直属部门</h2>
              <span class="internal-section-count">共 {{ filteredChildren.length }} 个</span>
            </div>
            <div class="internal-section-actions">
              <el-input
                v-model.trim="keyword"
                clearable
                size="small"
                prefix-icon="el-icon-search"
                placeholder="搜索直属部门"
                class="internal-keyword"
              />
              <el-button type="primary" size="small" icon="el-icon-plus" :disabled="!canAdd" @click="openAdd">
                新增行政部门
              </el-button>
            </div>
          </div>

          <div class="tbl-scroll-host">
          <el-table
            v-loading="loading"
            :data="filteredChildren"
            stripe
            class="internal-table"
            height="100%"
            empty-text="当前节点暂无直属行政部门"
          >
            <el-table-column type="index" label="序号" width="64" align="center" />
            <el-table-column label="ID" width="140" align="center" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span>{{ row.value || row.Id || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="label" label="部门名称" min-width="180" show-overflow-tooltip />
            <el-table-column label="节点类型" width="120" align="center">
              <template slot-scope="{ row }">
                <el-tag size="small" :type="isReadOnlyNode(row) ? 'info' : ''" effect="plain">
                  {{ nodeTypeText(row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="Leader" label="负责人" min-width="120" show-overflow-tooltip />
            <el-table-column prop="ThirdPartId" label="第三方 ID" min-width="140" show-overflow-tooltip />
            <el-table-column prop="OrderSort" label="排序" width="80" align="center" />
            <el-table-column label="操作" width="200" align="center">
              <template slot-scope="{ row }">
                <div class="table-op-pair">
                  <el-button type="text" class="table-op-link" :disabled="isReadOnlyNode(row)" @click="openEdit(row)">编辑</el-button>
                  <el-button type="text" class="table-op-link table-op-link--danger" :disabled="isReadOnlyNode(row)" @click="removeRow(row)">删除</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
          </div>
        </div>
      </div>
    </section>

    <el-dialog :title="formMode === 'add' ? '新增行政部门' : '编辑行政部门'" :visible.sync="formVisible"
      width="560px" :close-on-click-modal="false">
      <el-form ref="departmentForm" :model="form" :rules="formRules" label-width="100px">
        <el-form-item label="部门名称" prop="Name">
          <el-input v-model.trim="form.Name" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="上级节点" prop="Pid">
          <el-cascader v-model="form.Pid" :options="parentOptions" :props="cascaderProps" filterable
            style="width: 100%" placeholder="请选择学校或行政部门" />
        </el-form-item>
        <el-form-item label="负责人">
          <el-input v-model.trim="form.Leader" maxlength="30" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.OrderSort" :min="0" :max="9999" controls-position="right" />
        </el-form-item>
        <el-form-item label="第三方 ID">
          <el-input v-model.trim="form.ThirdPartId" maxlength="100" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>

    <!-- 与教学架构一致：导入组织 / 后台任务 两大 Tab。dingTreeKey 避免勾选残留。最后更新：2026-10-29 -->
    <el-dialog
      title="同步钉钉数据"
      :visible.sync="syncDialogVisible"
      width="680px"
      custom-class="internal-sync-dialog teaching-sync-dialog"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="teaching-sync-panel">
      <div class="internal-sync-tabs">
        <button type="button" :class="{ 'is-active': syncMode === 'department' }"
          @click="changeSyncMode('department')">
          <i class="el-icon-office-building"></i>
          <span><strong>导入钉钉组织</strong><small>勾选分支后提交同步中心任务</small></span>
        </button>
        <button type="button" :class="{ 'is-active': syncMode === 'task' }"
          @click="changeSyncMode('task')">
          <i class="el-icon-refresh"></i>
          <span><strong>后台同步任务</strong><small>更新教师数据</small></span>
        </button>
      </div>

      <div v-show="syncMode === 'department'" class="teaching-sync-tab-pane">
        <div class="internal-sync-target">
          <span><i class="el-icon-location-outline"></i> 挂载到本地</span>
          <strong>{{ (currentNode && currentNode.label) || '当前节点' }}</strong>
          <el-tag size="mini" type="primary" effect="plain">内部通讯录</el-tag>
        </div>

        <section class="internal-sync-tree">
          <header>
            <div>
              <strong>选择钉钉子部门</strong>
              <span>可勾选一个或多个分支</span>
              <span v-if="dingTreeCacheHint" class="ding-tree-cache-hint">{{ dingTreeCacheHint }}</span>
            </div>
            <div class="ding-tree-header-actions">
              <el-tooltip content="跳过缓存，从钉钉重新拉取最新组织" placement="top">
                <el-button type="text" icon="el-icon-refresh" :disabled="dingTreeLoading" :loading="dingTreeLoading"
                  @click="fetchDingTree(true)">刷新组织</el-button>
              </el-tooltip>
            </div>
          </header>
          <div class="internal-sync-tree__body" v-loading="dingTreeLoading"
            element-loading-text="正在拉取钉钉内部组织，请稍候...">
            <el-tree
              :key="'ding-tree-' + dingTreeKey"
              :data="dingTreeData"
              :props="{ label: 'DeptName', children: 'Children' }"
              node-key="DeptId"
              ref="dingTree"
              show-checkbox
              check-on-click-node
              :expand-on-click-node="false"
              class="custom-ding-tree"
            />
            <div v-if="!dingTreeLoading && !dingTreeData.length" class="internal-sync-tree__empty">
              <i class="el-icon-receiving"></i>
              <span>暂无可选的钉钉部门</span>
            </div>
          </div>
        </section>

        <div class="internal-sync-notice">
          <i class="el-icon-info"></i>
          <p>所选分支及其上级结构将一并同步到当前本地部门，任务会进入钉钉同步中心。内部通讯录不写入家校校区节点。</p>
        </div>
      </div>

      <div v-show="syncMode === 'task'" class="teaching-sync-tab-pane">
        <div class="internal-sync-target">
          <span><i class="el-icon-s-custom"></i> 选择同步内容</span>
          <strong>教师</strong>
          <el-tag size="mini" type="primary" effect="plain">内部通讯录</el-tag>
        </div>

        <section class="internal-sync-scope-tree">
          <header>
            <div>
              <strong>选择同步范围</strong>
              <span>勾选本地内部部门；同步钉钉企业内部通讯录成员（非家校任教）</span>
            </div>
          </header>
          <div class="internal-sync-scope-tree__body">
            <el-tree
              v-if="syncScopeTreeNodes.length"
              ref="syncScopeTree"
              :key="'sync-scope-' + syncScopeTreeKey"
              :data="syncScopeTreeNodes"
              :props="{ label: 'label', children: 'children' }"
              node-key="value"
              show-checkbox
              check-on-click-node
              :expand-on-click-node="false"
              :default-expanded-keys="syncScopeTreeExpandedKeys"
              class="custom-ding-tree"
            />
            <div v-else class="internal-sync-tree__empty">
              <i class="el-icon-receiving"></i>
              <span>暂无可选的内部组织节点</span>
            </div>
          </div>
        </section>

        <div class="internal-sync-notice">
          <i class="el-icon-info"></i>
          <p>组织请到「导入钉钉组织」勾选分支；此处同步内部通讯录教师（主档与部门关系）。钉钉/微信教师端登录须先在教学架构完成家校任教同步。</p>
        </div>
      </div>
      </div>

      <div slot="footer" class="internal-sync-footer">
        <span class="internal-sync-footer__hint">
          <i class="el-icon-info"></i>
          提交后可在钉钉同步中心查看进度
        </span>
        <div class="internal-sync-footer__actions">
          <el-button type="text" icon="el-icon-s-operation" @click="openTaskCenterFromSync">打开同步中心</el-button>
          <el-button @click="syncDialogVisible = false">取消</el-button>
          <el-button
            v-if="syncMode === 'department'"
            type="primary"
            icon="el-icon-upload2"
            :loading="!!syncLoading"
            :disabled="dingTreeLoading"
            @click="confirmSyncDing"
          >提交导入任务</el-button>
          <el-button
            v-else
            type="primary"
            icon="el-icon-upload2"
            :loading="!!syncLoading"
            @click="submitInternalSync"
          >提交任务</el-button>
        </div>
      </div>
    </el-dialog>

    <ding-talk-sync-task-center-dialog
      :visible.sync="taskCenterVisible"
      :school-department-id="currentSchoolId"
      :school-name="currentSchoolName"
      :big-type="0"
    />
  </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane'
import DingTalkSyncTaskCenterDialog from '../../components/DingTalkSyncTaskCenterDialog'
import { buildSyncScopeConfirmMessage } from '../../utils/dingTalkSyncScope'
import util from '../../../util/date'
import {
  addDepartment,
  editDepartment,
  removeDepartment,
  getInternalDepartmentTree,
  getDingDingDepartmentTree,
  syncSelectedDingDingDepartment,
  submitDingTalkSyncTask,
} from '../../api/modules/department'
import { resolveActiveSchoolId, normalizeDeptId, deptIdsEqual } from '../../utils/deptCascaderProps'

export default {
  name: 'InternalDept',
  components: { DepartmentPane, DingTalkSyncTaskCenterDialog },
  data() {
    return {
      loading: false,
      saving: false,
      removing: false,
      syncLoading: '',
      syncDialogVisible: false,
      syncMode: 'department',
      dingTreeLoading: false,
      dingTreeData: [],
      /** 每次打开/刷新钉钉树递增，强制重建避免勾选残留。最后更新：2026-10-29 */
      dingTreeKey: 0,
      dingTreeFromCache: false,
      dingTreeCachedAt: '',
      dingTreeExpiresAt: '',
      keyword: '',
      treeRoot: null,
      currentNode: null,
      currentSchoolId: 0,
      currentSchoolSchType: '',
      currentSchoolName: '',
      taskCenterVisible: false,
      syncScopeTreeKey: 0,
      syncScopeTreeNodes: [],
      syncScopeTreeExpandedKeys: [],
      formVisible: false,
      formMode: 'add',
      form: this.createEmptyForm(),
      formRules: {
        Name: [{ required: true, message: '请输入部门名称', trigger: 'blur' }],
        Pid: [{ required: true, message: '请选择上级节点', trigger: 'change' }],
      },
      cascaderProps: {
        value: 'value',
        label: 'label',
        children: 'children',
        emitPath: false,
        checkStrictly: true,
      },
    }
  },
  computed: {
    filteredChildren() {
      const children = (this.currentNode && this.currentNode.children) || []
      const keyword = this.keyword.toLowerCase()
      if (!keyword) return children
      return children.filter(item => String(item.label || '').toLowerCase().indexOf(keyword) !== -1)
    },
    parentOptions() {
      return this.treeRoot ? [this.cloneParentTree(this.treeRoot)].filter(Boolean) : []
    },
    canAdd() {
      return !!(this.currentNode && normalizeDeptId(this.currentNode.value) && !this.isCampusNode(this.currentNode))
    },
    /** 当前选中节点是否为可管理的行政部门 */
    canManageCurrent() {
      return !!(this.currentNode && normalizeDeptId(this.currentNode.value) && !this.isReadOnlyNode(this.currentNode))
    },
    /** 仅钉钉学校显示同步入口。最后更新：2026-08-30 */
    isDingTalkSchool() {
      return String(this.currentSchoolSchType || '').toLowerCase() === 'dingding'
    },
    /** 钉钉组织树缓存提示文案。最后更新：2026-08-30 */
    dingTreeCacheHint() {
      if (this.dingTreeLoading || !this.dingTreeData.length) return ''
      const timeText = this.formatDingTreeCacheTime(this.dingTreeCachedAt)
      const expireText = this.formatDingTreeCacheTime(this.dingTreeExpiresAt)
      const expireSuffix = expireText ? ` · 将于 ${expireText} 过期` : ''
      if (this.dingTreeFromCache && timeText) return `缓存数据 · 更新于 ${timeText}${expireSuffix}`
      if (timeText) return `已实时拉取 · ${timeText}${expireSuffix}`
      return expireText ? `已实时拉取${expireSuffix}` : '已实时拉取'
    },
  },
  mounted() {
    this.loadTree()
  },
  methods: {
    /** 创建空白行政部门表单 */
    createEmptyForm() {
      return { Id: 0, Name: '', Pid: '', BigType: 0, Leader: '', OrderSort: 0, ThirdPartId: '' }
    },
    /** 读取固定内部范围组织树 */
    async loadTree(preferredId) {
      this.loading = true
      try {
        const activeSchoolId = resolveActiveSchoolId(this)
        this.currentSchoolId = activeSchoolId || ''
        if (!this.currentSchoolId) {
          this.treeRoot = null
          this.currentNode = null
          return
        }
        const res = await getInternalDepartmentTree({ pid: 0, scopeDepartmentId: activeSchoolId })
        let tree = res.data && res.data.response
        // 与 DepartmentPane 一致：内部树默认去掉校区。最后更新：2026-08-30
        tree = this.normalizeInternalTree(tree)
        this.treeRoot = tree
        if (preferredId) this.currentNode = this.findNode(this.treeRoot, preferredId)
        if (!this.currentNode) this.currentNode = this.treeRoot
        this.applySchoolMetaFromNode(this.currentNode || this.treeRoot)
      } finally {
        this.loading = false
      }
    },
    /** 从树节点向上解析学校 SchType / 名称 */
    applySchoolMetaFromNode(nodeOrData) {
      let current = nodeOrData
      // treeNodeClick 传入的是 el-tree node，带 parent；loadTree 传入的是数据节点
      if (current && current.data && (current.parent || current.level !== undefined)) {
        while (current) {
          const item = current.data || {}
          const type = String(item.DeptOrgType || item.deptOrgType || '').toLowerCase()
          if (type === 'school' || type === 'affiliateschool') {
            this.currentSchoolId = normalizeDeptId(item.value) || this.currentSchoolId
            this.currentSchoolSchType = item.SchType || item.schType || ''
            this.currentSchoolName = item.label || item.Name || this.$store.state.activeSchoolName || ''
            return
          }
          current = current.parent
        }
        return
      }
      // 数据节点：若本身是学校，或用 treeRoot
      const data = current || this.treeRoot
      if (!data) {
        this.currentSchoolSchType = ''
        this.currentSchoolName = ''
        return
      }
      const type = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase()
      if (type === 'school' || type === 'affiliateschool') {
        this.currentSchoolId = normalizeDeptId(data.value) || this.currentSchoolId
        this.currentSchoolSchType = data.SchType || data.schType || ''
        this.currentSchoolName = data.label || data.Name || this.$store.state.activeSchoolName || ''
        return
      }
      const school = this.findSchoolInTree(this.treeRoot)
      if (school) {
        this.currentSchoolId = normalizeDeptId(school.value) || this.currentSchoolId
        this.currentSchoolSchType = school.SchType || school.schType || ''
        this.currentSchoolName = school.label || school.Name || this.$store.state.activeSchoolName || ''
      }
    },
    findSchoolInTree(node) {
      if (!node) return null
      const type = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
      if (type === 'school' || type === 'affiliateschool') return node
      const children = node.children || []
      for (let i = 0; i < children.length; i += 1) {
        const found = this.findSchoolInTree(children[i])
        if (found) return found
      }
      return null
    },
    /** 剔除校区等家校节点，子行政部门抬升保留 */
    normalizeInternalTree(node) {
      if (!node) return null
      const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
      if (['campus', 'period', 'grade', 'class'].indexOf(orgType) !== -1) {
        const lifted = []
        ;(node.children || []).forEach(child => {
          const normalized = this.normalizeInternalTree(child)
          if (Array.isArray(normalized)) lifted.push(...normalized)
          else if (normalized) lifted.push(normalized)
        })
        return lifted
      }
      const copy = Object.assign({}, node)
      const nextChildren = []
      ;(node.children || []).forEach(child => {
        const normalized = this.normalizeInternalTree(child)
        if (Array.isArray(normalized)) nextChildren.push(...normalized)
        else if (normalized) nextChildren.push(normalized)
      })
      copy.children = nextChildren
      return copy
    },
    treeNodeClick(data, node) {
      this.currentNode = data
      this.keyword = ''
      this.applySchoolMetaFromNode(node || data)
    },
    findNode(node, id) {
      if (!node) return null
      if (String(node.value) === String(id)) return node
      const children = node.children || []
      for (let index = 0; index < children.length; index += 1) {
        const found = this.findNode(children[index], id)
        if (found) return found
      }
      return null
    },
    cloneParentTree(node) {
      if (!node || this.isCampusNode(node)) return null
      const copy = Object.assign({}, node)
      copy.children = (node.children || [])
        .map(child => this.cloneParentTree(child))
        .filter(Boolean)
      return copy
    },
    nodeTypeText(node) {
      const type = String((node && (node.DeptOrgType || node.deptOrgType)) || '').toLowerCase()
      if (type === 'school' || type === 'affiliateschool') return '学校（只读）'
      if (type === 'campus') return '校区（家校）'
      return '行政部门'
    },
    isCampusNode(node) {
      const type = String((node && (node.DeptOrgType || node.deptOrgType)) || '').toLowerCase()
      return type === 'campus'
    },
    isReadOnlyNode(node) {
      const type = String((node && (node.DeptOrgType || node.deptOrgType)) || '').toLowerCase()
      return ['school', 'affiliateschool', 'campus', 'platform'].indexOf(type) !== -1
    },
    openAdd() {
      if (!this.canAdd) return
      this.formMode = 'add'
      this.form = Object.assign(this.createEmptyForm(), { Pid: this.currentNode.value })
      this.formVisible = true
    },
    openEdit(row) {
      if (this.isReadOnlyNode(row)) return
      this.formMode = 'edit'
      this.form = {
        Id: row.Id || row.value,
        Name: row.Name || row.label,
        Pid: row.Pid,
        BigType: 0,
        Leader: row.Leader || '',
        OrderSort: Number(row.OrderSort || 0),
        ThirdPartId: row.ThirdPartId || '',
      }
      this.formVisible = true
    },
    /** 编辑左侧当前选中的行政部门 */
    openEditCurrent() {
      if (!this.canManageCurrent) return
      this.openEdit(this.currentNode)
    },
    submitForm() {
      this.$refs.departmentForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = Object.assign({}, this.form, { BigType: 0 })
          const request = this.formMode === 'add' ? addDepartment(payload) : editDepartment(payload)
          const res = await request
          if (!res.data.success) {
            this.$message.error(res.data.msg || '保存失败')
            return
          }
          this.$message.success(res.data.msg || '保存成功')
          this.formVisible = false
          await this.loadTree(payload.Pid)
          this.$refs.departmentPane.refreshMethod(payload.Pid)
        } finally {
          this.saving = false
        }
      })
    },
    async removeRow(row) {
      if (this.isReadOnlyNode(row)) return
      const childCount = (row.children || []).length
      if (childCount > 0) {
        this.$message.warning(`「${row.label}」下还有 ${childCount} 个直属部门，请先删除下级`)
        return
      }
      await this.$confirm(`确认删除行政部门【${row.label}】吗？`, '提示', { type: 'warning' })
      this.removing = true
      try {
        const res = await removeDepartment({ id: row.Id || row.value })
        if (!res.data.success) {
          this.$message.error(res.data.msg || '删除失败')
          return
        }
        this.$message.success('删除成功')
        const parentId = row.Pid || (this.currentNode && this.currentNode.value)
        await this.loadTree(parentId)
        this.$refs.departmentPane.refreshMethod(parentId)
      } finally {
        this.removing = false
      }
    },
    /** 删除左侧当前选中的行政部门 */
    async removeCurrent() {
      if (!this.canManageCurrent) return
      const row = this.currentNode
      const childCount = (row.children || []).length
      if (childCount > 0) {
        this.$message.warning(`「${row.label}」下还有 ${childCount} 个直属部门，请先删除下级`)
        return
      }
      await this.$confirm(`确认删除当前行政部门【${row.label}】吗？`, '提示', { type: 'warning' })
      this.removing = true
      try {
        const res = await removeDepartment({ id: row.Id || row.value })
        if (!res.data.success) {
          this.$message.error(res.data.msg || '删除失败')
          return
        }
        this.$message.success('删除成功')
        const parentId = row.Pid || this.currentSchoolId
        await this.loadTree(parentId)
        this.$refs.departmentPane.refreshMethod(parentId)
      } finally {
        this.removing = false
      }
    },
    /** 打开内部通讯录同步弹窗。最后更新：2026-10-29 */
    openSyncDialog() {
      if (!this.isDingTalkSchool) {
        this.$message.warning('仅钉钉学校可同步钉钉数据')
        return
      }
      if (!this.currentNode || !normalizeDeptId(this.currentNode.value)) {
        this.$message.warning('请先选择具体学校或行政部门')
        return
      }
      this.syncMode = 'department'
      this.dingTreeData = []
      this.dingTreeKey += 1
      this.dingTreeFromCache = false
      this.dingTreeCachedAt = ''
      this.dingTreeExpiresAt = ''
      this.syncScopeTreeNodes = []
      this.syncScopeTreeExpandedKeys = []
      this.syncScopeTreeKey += 1
      this.syncDialogVisible = true
      this.$nextTick(() => this.fetchDingTree())
    },
    /** 切换组织导入或后台任务 */
    changeSyncMode(mode) {
      this.syncMode = mode
      if (mode === 'department' && this.dingTreeData.length === 0) {
        this.$nextTick(() => this.fetchDingTree())
      }
      if (mode === 'task') {
        if (!this.syncScopeTreeNodes.length) {
          this.refreshSyncScopeTree()
        }
        this.$nextTick(() => this.initSyncScopeTreeSelection())
      }
    },
    /** 刷新同步范围树数据。最后更新：2026-08-30 */
    refreshSyncScopeTree() {
      this.syncScopeTreeNodes = this.resolveSyncScopeTreeNodes()
      this.syncScopeTreeExpandedKeys = this.syncScopeTreeNodes
        .map((node) => node && node.value)
        .filter((value) => value != null && value !== '')
        .map((value) => String(value))
    },
    /** 克隆组织树供弹窗 el-tree 使用。最后更新：2026-08-30 */
    cloneSyncScopeNode(node) {
      if (!node || typeof node !== 'object' || Array.isArray(node)) return node
      return Object.assign({}, node, {
        children: (node.children || []).map((child) => this.cloneSyncScopeNode(child)),
      })
    },
    /** 解析内部通讯录同步范围树。最后更新：2026-08-30 */
    resolveSyncScopeTreeNodes() {
      const pane = this.$refs.departmentPane
      const menuNodes = pane && Array.isArray(pane.menus)
        ? pane.menus.filter((node) => normalizeDeptId(node && node.value))
        : []
      if (menuNodes.length) {
        return menuNodes.map((node) => this.cloneSyncScopeNode(node))
      }
      const root = pane && pane.originalTree
      if (root) {
        const activeSchoolId = normalizeDeptId(this.currentSchoolId)
          || resolveActiveSchoolId(this)
        if (Array.isArray(root)) {
          for (let index = 0; index < root.length; index += 1) {
            const schoolNode = pane.findSchoolNode
              ? pane.findSchoolNode(root[index], activeSchoolId)
              : root[index]
            if (schoolNode && normalizeDeptId(schoolNode.value)) {
              return [this.cloneSyncScopeNode(schoolNode)]
            }
          }
          return root.map((node) => this.cloneSyncScopeNode(node))
        }
        const schoolNode = pane.findSchoolNode
          ? pane.findSchoolNode(root, activeSchoolId)
          : root
        if (schoolNode) return [this.cloneSyncScopeNode(schoolNode)]
      }
      if (!this.treeRoot) return []
      if (Array.isArray(this.treeRoot)) {
        return this.treeRoot.map((node) => this.cloneSyncScopeNode(node))
      }
      return [this.cloneSyncScopeNode(this.treeRoot)]
    },
    /** 初始化教师同步范围树：默认勾选当前左侧部门（学校根则不勾选）。最后更新：2026-08-30 */
    initSyncScopeTreeSelection() {
      this.$nextTick(() => {
        const tree = this.$refs.syncScopeTree
        if (!tree) return
        const currentId = normalizeDeptId(this.currentNode && this.currentNode.value)
        const schoolId = normalizeDeptId(this.currentSchoolId)
        if (currentId && schoolId && !deptIdsEqual(currentId, schoolId)) {
          tree.setCheckedKeys([String(currentId)])
        } else {
          tree.setCheckedKeys([])
        }
      })
    },
    buildInternalSyncScopePayload() {
      const tree = this.$refs.syncScopeTree
      if (!tree) return null
      const checkedNodes = tree.getCheckedNodes(false, false)
      if (!checkedNodes.length) return null
      const schoolId = normalizeDeptId(this.currentSchoolId)
      if (checkedNodes.length === 1 && deptIdsEqual(checkedNodes[0].value, schoolId)) {
        return { scopeType: 'School', scopeDepartmentIds: [] }
      }
      const scopeDepartmentIds = checkedNodes
        .map((node) => normalizeDeptId(node.value))
        .filter((id) => !!id)
      if (!scopeDepartmentIds.length) return null
      return { scopeType: 'Department', scopeDepartmentIds }
    },
    /** 拉取钉钉企业内部通讯录组织树；forceRefresh 为 true 时跳过缓存。最后更新：2026-10-29 */
    fetchDingTree(forceRefresh = false) {
      if (!this.currentNode || this.dingTreeLoading) return
      this.dingTreeLoading = true
      if (forceRefresh) {
        this.dingTreeData = []
        this.dingTreeKey += 1
        this.dingTreeFromCache = false
        this.dingTreeCachedAt = ''
        this.dingTreeExpiresAt = ''
      }
      getDingDingDepartmentTree({
        id: this.currentNode.value,
        type: 0,
        forceRefresh: !!forceRefresh,
      }).then((res) => {
        if (util.isEmt.format(res)) return
        if (res.data.success) {
          this.applyDingTreePayload(res.data.response)
        } else {
          this.$message.error(res.data.msg || '拉取钉钉组织失败')
        }
      }).finally(() => {
        this.dingTreeLoading = false
        this.$nextTick(() => this.clearDingTreeCheckedKeys())
      })
    },
    /** 清空钉钉组织树勾选，避免跨次打开残留。最后更新：2026-10-29 */
    clearDingTreeCheckedKeys() {
      const dingTree = this.$refs.dingTree
      if (dingTree && typeof dingTree.setCheckedKeys === 'function') {
        dingTree.setCheckedKeys([])
      }
    },
    applyDingTreePayload(payload) {
      if (!payload) {
        this.dingTreeData = []
        return
      }
      if (Array.isArray(payload)) {
        this.dingTreeData = payload
        this.dingTreeFromCache = false
        this.dingTreeCachedAt = ''
        this.dingTreeExpiresAt = ''
        return
      }
      this.dingTreeData = payload.Tree || payload.tree || []
      this.dingTreeFromCache = !!(payload.FromCache != null ? payload.FromCache : payload.fromCache)
      this.dingTreeCachedAt = payload.CachedAt || payload.cachedAt || ''
      this.dingTreeExpiresAt = payload.ExpiresAt || payload.expiresAt || ''
    },
    formatDingTreeCacheTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = (num) => String(num).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    /** 勾选导入钉钉内部组织到当前本地节点。最后更新：2026-10-29 */
    confirmSyncDing() {
      if (this.dingTreeLoading) {
        this.$message.warning('钉钉组织加载中，请稍后再提交')
        return
      }
      const dingTree = this.$refs.dingTree
      const checkedNodes = dingTree ? dingTree.getCheckedNodes(false, true) : []
      if (!checkedNodes || checkedNodes.length === 0) {
        this.$message.warning('请先勾选要同步的钉钉部门')
        return
      }
      const strictCheckedNodes = dingTree.getCheckedNodes(false, false)
      const para = {
        id: this.currentNode.value,
        dingDeptId: checkedNodes.map(node => node.DeptId).join(','),
        type: 0,
        strictDingDeptId: strictCheckedNodes.map(node => node.DeptId).join(','),
      }
      this.$confirm(
        `确认将所选钉钉分支导入到【${this.currentNode.label}】下吗？`,
        '确认提交',
        { type: 'warning', confirmButtonText: '提交任务', cancelButtonText: '取消' }
      ).then(() => {
        this.syncLoading = 'Department'
        return syncSelectedDingDingDepartment(para)
      }).then((res) => {
        if (!res || util.isEmt.format(res)) return
        if (res.data.success) {
          const taskId = res.data.response
          this.$message.success(taskId ? `导入任务 ${taskId} 已提交` : (res.data.msg || '导入任务已提交'))
          this.syncDialogVisible = false
          this.openTaskCenter()
          return
        }
        this.$message({
          message: `${res.data.msg ? res.data.msg.replace(/\n/g, '<br>') : '导入失败'}`,
          type: 'error',
          dangerouslyUseHTMLString: true,
        })
      }).catch(() => {}).finally(() => {
        this.syncLoading = ''
      })
    },
    openTaskCenterFromSync() {
      this.syncDialogVisible = false
      this.openTaskCenter()
    },
    async submitInternalSync() {
      await this.runSync('Teacher')
    },
    async runSync(resourceType) {
      if (!this.isDingTalkSchool) {
        this.$message.warning('仅钉钉学校可同步钉钉数据')
        return
      }
      if (!this.currentNode || Number(this.currentNode.value) <= 0) {
        this.$message.warning('请先选择具体学校或行政部门')
        return
      }
      if (!this.currentSchoolId) {
        this.$message.warning('未识别到当前节点所属学校')
        return
      }
      const scopePayload = this.buildInternalSyncScopePayload()
      if (!scopePayload) {
        this.$message.warning('请先勾选同步范围')
        return
      }
      await this.$confirm(
        buildSyncScopeConfirmMessage(scopePayload, '教师'),
        '同步确认',
        { type: 'warning' }
      )
      this.syncLoading = resourceType
      try {
        const res = await submitDingTalkSyncTask({
          SchoolDepartmentId: this.currentSchoolId,
          ResourceType: resourceType,
          BigType: 0,
          ScopeType: scopePayload.scopeType,
          ScopeDepartmentIds: scopePayload.scopeDepartmentIds,
        })
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '同步任务提交失败')
          return
        }
        const taskId = res.data.response
        this.$message.success(`同步任务 ${taskId} 已提交`)
        this.syncDialogVisible = false
        this.openTaskCenter()
      } finally {
        this.syncLoading = ''
      }
    },
    /** 弹窗打开钉钉同步中心，避免跳转离开当前页 */
    openTaskCenter() {
      this.taskCenterVisible = true
    },
  },
}
</script>

<style scoped>
/* 右侧对齐教学架构页 workspace 结构。最后修改：2026-08-29 */
.internal-directory-page {
  --id-primary: var(--btn-primary, #2563eb);
  --id-soft: var(--btn-primary-plain-bg, #eff6ff);
  --id-ink: #1e293b;
  --id-muted: #64748b;
  --id-line: #e8edf3;
  --id-radius: 10px;
}

.internal-directory-page > .dp-right {
  padding: 0;
  overflow: hidden;
}

.internal-workspace {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
}

.internal-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  flex: 0 0 auto;
  /* 与左侧 DepartmentPane.pane-head 顶距对齐。最后修改：2026-08-29 */
  padding: 12px 18px 12px;
  border-bottom: 1px solid var(--id-line);
}

.internal-context {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.internal-title-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: var(--id-primary);
  background: var(--id-soft);
  font-size: 18px;
}

.internal-context h1 {
  margin: 0 0 4px;
  color: var(--id-ink);
  font-size: 18px;
  font-weight: 600;
}

.internal-subtitle {
  margin: 0 0 8px;
  color: var(--id-muted);
  font-size: 12px;
  line-height: 1.5;
}

.internal-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: var(--id-muted);
  font-size: 12px;
}

.internal-breadcrumb strong {
  max-width: 280px;
  overflow: hidden;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.internal-type-tag {
  margin-left: 2px;
}

.internal-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.internal-actions >>> .el-button + .el-button {
  margin-left: 0;
}

.internal-list-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 14px 18px 18px;
  overflow: hidden;
}

.internal-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.internal-section-heading > div:first-child {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.internal-section-heading h2 {
  margin: 0;
  color: var(--id-ink);
  font-size: 16px;
  font-weight: 700;
}

.internal-section-count {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--id-soft);
  color: var(--id-primary);
  font-size: 12px;
  font-weight: 600;
}

.internal-section-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.internal-keyword {
  width: 220px;
}

.internal-table {
  width: 100%;
  border: 1px solid var(--id-line);
  border-radius: var(--id-radius);
  overflow: hidden;
}

.internal-table >>> th {
  height: 44px;
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.internal-table >>> td {
  height: 52px;
}

.internal-table >>> .el-table__row:hover > td {
  background: #f8fafc !important;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}

@media screen and (max-width: 768px) {
  .internal-keyword {
    width: 100%;
  }

  .internal-section-actions {
    width: 100%;
  }
}

.internal-sync-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.internal-sync-tabs button {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}

.internal-sync-tabs button:hover {
  border-color: #93c5fd;
  background: #f8fbff;
}

.internal-sync-tabs button.is-active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.15);
}

.internal-sync-tabs button > i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 16px;
  flex: 0 0 auto;
}

.internal-sync-tabs button.is-active > i {
  background: #dbeafe;
  color: #2563eb;
}

.internal-sync-tabs button span,
.internal-sync-tabs button strong,
.internal-sync-tabs button small {
  display: block;
}

.internal-sync-tabs button span {
  min-width: 0;
}

.internal-sync-tabs button strong {
  color: #334155;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.internal-sync-tabs button.is-active strong {
  color: #1d4ed8;
}

.internal-sync-tabs button small {
  margin-top: 2px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.4;
}

.internal-sync-target {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
  gap: 8px 10px;
  min-height: 44px;
  height: 44px;
  margin-bottom: 12px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
}

.internal-sync-target > span i {
  margin-right: 2px;
}

.internal-sync-target > strong {
  color: #1e293b;
  font-weight: 600;
}

.internal-sync-target .el-tag {
  margin-left: auto;
  flex-shrink: 0;
}

.internal-sync-scope-tree {
  overflow: hidden;
  flex: 1;
  min-height: 0;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.internal-sync-scope-tree > header {
  padding: 10px 13px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
}

.internal-sync-scope-tree > header strong,
.internal-sync-scope-tree > header span {
  display: block;
}

.internal-sync-scope-tree > header strong {
  color: #1e293b;
  font-size: 14px;
}

.internal-sync-scope-tree > header span {
  margin-top: 2px;
  color: #94a3b8;
  font-size: 12px;
}

.internal-sync-scope-tree__body {
  flex: 1;
  min-height: 0;
  padding: 8px 10px;
  overflow: auto;
}

.internal-sync-tree {
  border: 1px solid #e8edf3;
  border-radius: 12px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.internal-sync-tree > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef2f7;
  background: #fbfcfe;
}

.internal-sync-tree > header strong,
.internal-sync-tree > header span {
  display: block;
}

.internal-sync-tree > header strong {
  color: #1e293b;
  font-size: 14px;
}

.internal-sync-tree > header span {
  margin-top: 2px;
  color: #94a3b8;
  font-size: 12px;
}

.ding-tree-cache-hint {
  display: block;
  margin-top: 4px;
  color: #64748b !important;
  font-size: 11px !important;
}

.ding-tree-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.internal-sync-tree__body {
  position: relative;
  flex: 1;
  min-height: 0;
  padding: 8px 10px;
  overflow: auto;
}

.internal-sync-tree .el-tree-node__content {
  height: 34px;
  border-radius: 6px;
}

.internal-sync-tree .el-tree-node__content:hover {
  background: #f8fafc;
}

.internal-sync-tree__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 200px;
  color: #94a3b8;
  font-size: 13px;
}

.internal-sync-tree__empty i {
  font-size: 28px;
}

.internal-sync-notice {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  min-height: 56px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 12px;
  line-height: 1.55;
  box-sizing: border-box;
}

.internal-sync-notice > i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.internal-sync-notice p {
  margin: 0;
}

.internal-sync-resource__head {
  margin-bottom: 12px;
}

.internal-sync-resource__head strong {
  display: block;
  color: #1e293b;
  font-size: 14px;
}

.internal-sync-resource__head span {
  display: block;
  margin-top: 4px;
  color: #94a3b8;
  font-size: 12px;
}

.internal-sync-options {
  display: flex;
  width: 100%;
}

.internal-sync-options--people >>> .el-radio-button {
  flex: 1;
}

.internal-sync-options >>> .el-radio-button {
  flex: 1;
}

.internal-sync-options >>> .el-radio-button__inner {
  width: 100%;
  padding: 12px 8px;
  border-radius: 0 !important;
}

.internal-sync-options >>> .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 8px 0 0 8px !important;
}

.internal-sync-options >>> .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 8px 8px 0 !important;
}

.internal-sync-options >>> .el-radio-button__inner i {
  margin-right: 4px;
}

.internal-sync-flow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 13px;
}

.internal-sync-flow > i {
  font-size: 12px;
}

.internal-sync-flow small {
  margin-left: auto;
  color: #64748b;
}

.internal-sync-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.internal-sync-footer__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

/* 已有 flex gap，避免与 dialog-theme 的相邻 margin 叠成双倍间距 */
.internal-sync-dialog .internal-sync-footer__actions .el-button + .el-button {
  margin-left: 0 !important;
}

.internal-sync-footer__hint {
  color: #94a3b8;
  font-size: 12px;
}

.internal-sync-footer__hint i {
  margin-right: 4px;
}

@media screen and (max-width: 768px) {
  .internal-sync-tree__body,
  .internal-sync-scope-tree__body {
    height: auto;
    flex: 1;
    min-height: 0;
  }

  .internal-sync-notice {
    margin-top: 8px;
    padding: 8px 10px;
    font-size: 11px;
  }
}

@media screen and (max-width: 640px) {
  .internal-sync-tabs {
    grid-template-columns: 1fr;
  }

  .internal-sync-footer__hint {
    width: 100%;
  }
}
</style>

<style>
/* 选择器需压过 dialog-theme 的 .el-dialog:not(.is-fullscreen)。最后修改：2026-09-14 */
.el-dialog.internal-sync-dialog.teaching-sync-dialog,
.el-dialog.teaching-sync-dialog {
  display: flex;
  flex-direction: column;
  width: 680px !important;
  max-width: calc(100vw - 32px);
  height: min(760px, calc(100vh - 48px));
  height: min(760px, calc(100dvh - 48px));
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  margin-top: calc((100vh - min(760px, calc(100vh - 48px))) / 2) !important;
  margin-top: calc((100dvh - min(760px, calc(100dvh - 48px))) / 2) !important;
  margin-bottom: 0 !important;
  border-radius: 14px;
  overflow: hidden;
}

.el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__header,
.el-dialog.teaching-sync-dialog .el-dialog__header {
  flex-shrink: 0;
  padding: 18px 22px 15px;
  border-bottom: 1px solid #e8edf3;
  background: #fbfcfe;
}

.el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__title,
.el-dialog.teaching-sync-dialog .el-dialog__title {
  color: #172033;
  font-size: 17px;
  font-weight: 600;
}

.el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__headerbtn,
.el-dialog.teaching-sync-dialog .el-dialog__headerbtn {
  top: 20px;
  right: 22px;
}

.el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__body,
.el-dialog.teaching-sync-dialog .el-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  padding: 16px 22px 10px;
  overflow: hidden;
}

.el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__footer,
.el-dialog.teaching-sync-dialog .el-dialog__footer {
  flex-shrink: 0;
  padding: 14px 22px 18px;
  border-top: 1px solid #eef2f6;
  background: #fbfcfe;
}

@media (max-width: 768px) {
  .el-dialog.internal-sync-dialog.teaching-sync-dialog,
  .el-dialog.teaching-sync-dialog {
    width: calc(100vw - 16px) !important;
    height: calc(100vh - 16px);
    height: calc(100dvh - 16px);
    max-height: none;
    margin: 8px auto !important;
    border-radius: 12px;
  }

  .el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__header,
  .el-dialog.teaching-sync-dialog .el-dialog__header {
    padding: 14px 16px 12px;
  }

  .el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__body,
  .el-dialog.teaching-sync-dialog .el-dialog__body {
    padding: 12px 14px 8px;
  }

  .el-dialog.internal-sync-dialog.teaching-sync-dialog .el-dialog__footer,
  .el-dialog.teaching-sync-dialog .el-dialog__footer {
    padding: 10px 14px 14px;
  }
}

.teaching-sync-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.teaching-sync-tab-pane {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
</style>
